import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ReferralProvider } from '@/types/referrals';
import { Building2, Check, ChevronsUpDown, Stethoscope } from 'lucide-react';
import { useState } from 'react';

interface Props {
  id: string;
  disabled: boolean;
  loading: boolean;
  rows: ReferralProvider[];
  value: string;
  onSelect: (value: string) => void;
}

export const ReferralRecipientSearch = ({ id, disabled, loading, rows, value, onSelect }: Props) => {
  const [open, setOpen] = useState(false);
  const grouped = new Map<string, ReferralProvider & { services: Set<string> }>();
  for (const row of rows) {
    const existing = grouped.get(row.id);
    if (existing) existing.services.add(row.service_name);
    else grouped.set(row.id, { ...row, services: new Set([row.service_name]) });
  }
  const recipients = [...grouped.values()];
  const selected = recipients.find((row) => row.id === value);
  return (
    <div className="space-y-1.5 sm:col-span-2">
      <Label htmlFor={id}>Stofnun eða læknir</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-haspopup="dialog"
            aria-controls={open ? `${id}-options` : undefined}
            aria-expanded={open}
            aria-label="Stofnun eða læknir"
            disabled={disabled || loading}
            className="h-auto min-h-10 w-full justify-between text-left font-normal"
          >
            {selected ? (
              <span className="flex min-w-0 items-center gap-2">
                {selected.recipient_type === 'institution' ? (
                  <Building2 aria-hidden="true" className="h-4 w-4 shrink-0 text-blue-600" />
                ) : (
                  <Stethoscope aria-hidden="true" className="h-4 w-4 shrink-0 text-violet-600" />
                )}
                <span className="truncate">{selected.name || selected.institute_name}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">
                {loading ? 'Sæki viðtakendur...' : 'Leita að stofnun eða lækni...'}
              </span>
            )}
            <ChevronsUpDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent id={`${id}-options`} align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command
            label="Leita að viðtakanda"
            filter={(_value, search, keywords = []) => {
              const normalize = (text: string) =>
                text
                  .normalize('NFD')
                  .replace(/\p{Diacritic}/gu, '')
                  .toLowerCase();
              return normalize(keywords.join(' ')).includes(normalize(search.trim())) ? 1 : 0;
            }}
          >
            <CommandInput
              aria-label="Leita að stofnun, lækni eða sérgrein"
              placeholder="Nafn, stofnun eða sérgrein..."
            />
            <CommandList>
              <CommandEmpty>Enginn viðtakandi fannst.</CommandEmpty>
              <CommandGroup heading="Viðtakendur">
                {recipients.map((row) => {
                  const institution = row.recipient_type === 'institution';
                  const Icon = institution ? Building2 : Stethoscope;
                  const specialties = [...row.services].join(' · ');
                  const details = institution ? specialties : `${row.institute_name} · ${specialties}`;
                  return (
                    <CommandItem
                      key={row.id}
                      value={row.id}
                      keywords={[row.name, row.institute_name, specialties]}
                      aria-label={`${row.name || row.institute_name}, ${institution ? 'Stofnun' : 'Læknir'}, ${details}`}
                      onSelect={() => {
                        onSelect(row.id);
                        setOpen(false);
                      }}
                    >
                      <Icon aria-hidden="true" className={institution ? 'text-blue-600' : 'text-violet-600'} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{row.name || row.institute_name}</span>
                        <span className="block truncate text-muted-foreground text-xs" title={details}>
                          {details}
                        </span>
                      </span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs ${institution ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}
                      >
                        {institution ? 'Stofnun' : 'Læknir'}
                      </span>
                      {value === row.id && <Check aria-hidden="true" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
